import { Text, Flex, Anchor, Loader, Badge } from '@mantine/core';
import classes from '../Admin.module.css';
import { useTranslation } from 'react-i18next';
import DevToolsCard from './DevToolsCard';
import NewTable from '../../../shared/components/NewTable/NewTable';
import { CellValue } from '../../../shared/components/NewTable/useColumns';
import { useNavigate } from 'react-router-dom';
import { useListWebhooks } from '../../../hooks/webhooksApi';
import { useWebhooksStats } from '../../../hooks/webhooksApi/useWebhookStats';
import { WEBHOOKS_API_DOCS } from '../../../common/constants';
import { BetaIcon } from '../../../../lib/components';

export const Webhooks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const listWebhooks = useListWebhooks();
  const webhooksStats = useWebhooksStats(
    listWebhooks.data?.map((webhook) => webhook.id) ?? []
  );

  const handleWebhookAdd = () => {
    navigate('/admin/dev-tools/webhooks/create');
  };

  return (
    <DevToolsCard title={t('devTools.webhooks.title')}>
      <Text fs="italic">{t('devTools.webhooks.webhooksDescription')}</Text>
      <Flex justify="space-between" align="center">
        <Text fs="italic">
          {t('devTools.webhooks.webhooksText')}{' '}
          <Anchor
            target="_blank"
            underline="hover"
            href={WEBHOOKS_API_DOCS}
            className={classes.link}
            rel="noopener"
          >
            {t('devTools.webhooks.docsLink')}
          </Anchor>
        </Text>
        <Anchor
          target="_blank"
          underline="hover"
          href={WEBHOOKS_API_DOCS}
          c="white"
          className={classes.iconLink}
          rel="noopener"
        >
          <BetaIcon icon="external-link" />
        </Anchor>
      </Flex>

      <NewTable
        height="auto"
        header={{
          title: t('devTools.webhooks.webhooksList'),
          onAdd: handleWebhookAdd,
        }}
        data={listWebhooks.data}
        onSelect={(id) => navigate(`/admin/dev-tools/webhooks/${id}`)}
        columns={[
          {
            value: 'url',
            label: t('devTools.webhooks.url'),
          },
          {
            value: 'id',
            label: t('devTools.webhooks.errorRate'),
            renderText: (id: CellValue, ...rest) => {
              const webhookId = id as string;
              if (!webhookId || !webhooksStats[webhookId]) {
                return <Loader size="xs" />;
              }

              return webhooksStats[webhookId]?.isLoading ? (
                <Loader size="xs" />
              ) : (
                `${webhooksStats[webhookId]?.data?.errorRate}%`
              );
            },
          },
          {
            value: 'isActive',
            label: t('devTools.webhooks.status'),
            renderText: (isActive: CellValue) => {
              return (
                <Badge color={isActive ? 'green' : '#7a7a7a'}>
                  {isActive
                    ? t('devTools.webhooks.active')
                    : t('devTools.webhooks.inactive')}
                </Badge>
              );
            },
          },
        ]}
        loading={listWebhooks.isLoading}
        empty={{
          title: t('devTools.webhooks.noWebhooks'),
          subtitle: t('devTools.webhooks.noWebhooksSubtitle'),
          onClick: handleWebhookAdd,
          icon: 'webhook',
        }}
        style={{
          marginTop: '2rem',
          top: 0,
          width: '100%',
          position: 'relative',
          left: 0,
          height: '80%',
        }}
      />
    </DevToolsCard>
  );
};
